import { ControlTypeAhead } from "aic";
import { ClinicalDatasetReadDto } from "functions/api/library/interface";

/**
 * Props for the PrimaryVariableSelector component.
 */
interface PrimaryVariableSelectorProps {
  datasets: ClinicalDatasetReadDto[];
  selectedValue: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * Component for selecting a primary variable in format "dataset.variable".
 * Displays all available variables from all datasets in a single dropdown,
 * following the pattern used in InteractiveFigure.
 *
 * @param props - Destructured parameter object
 * @param props.datasets - Available clinical datasets
 * @param props.selectedValue - Currently selected variable in "dataset.variable" format
 * @param props.onChange - Handler to update the selected variable
 * @param props.disabled - Optional disabled flag
 */
export const PrimaryVariableSelector = ({
  datasets,
  selectedValue,
  onChange,
  disabled = false,
}: PrimaryVariableSelectorProps): React.ReactElement => {
  /**
   * Generate list of all variables in "dataset.variable" format
   */
  const options = datasets.flatMap((ds) => {
    const vars = ds.clinicalVariables.map((v) => v.name);
    return vars.map((v) => ({ value: `${ds.name}.${v}` }));
  });

  const variableOptions = options.length > 0 ? options : [{ value: "No variables available" }];

  /**
   * Handler for primary variable selection
   *
   * @param value - Selected variable value in "dataset.variable" format
   */
  const handleChange = (value: string): void => {
    if (value !== "No variables available") {
      onChange(value);
    }
  };

  return (
    <ControlTypeAhead
      id="primary-variable-select"
      label="Primary Variable"
      labelWidth={3}
      value={selectedValue}
      setValue={handleChange}
      maxWidthCols={12}
      availableList={variableOptions}
      disabled={disabled}
    />
  );
};

PrimaryVariableSelector.displayName = "PrimaryVariableSelector";
